import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { openBooking } from "@/lib/api";
import AntMark from "@/components/AntMark";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
    <AntMark testId="nav-logo-ant" />
    <span className="font-display text-2xl tracking-tight uppercase">
      Ant<span className="text-black">Crawler</span>
    </span>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-black">
        <div className="flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
          <Logo />
          <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-xs font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-lime px-1 ${
                    isActive ? "bg-lime" : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="tel:+17723324038"
              data-testid="nav-call-button"
              className="hidden lg:inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest border-2 border-black px-3 py-2 transition-colors duration-200 hover:bg-black hover:text-lime"
            >
              <Phone size={14} /> 772-332-4038
            </a>
            <button
              onClick={openBooking}
              data-testid="nav-book-consultation-button"
              className="font-mono text-xs font-bold uppercase tracking-widest bg-lime border-2 border-black px-4 py-2 shadow-[4px_4px_0_0_#0A0A0A] transition-[transform,box-shadow,background-color,color] duration-200 hover:bg-black hover:text-lime active:translate-x-[4px] active:translate-y-[4px] active:shadow-none"
            >
              Book a Consultation
            </button>
          </nav>
          <button
            className="md:hidden p-2"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-ink text-white flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b-2 border-lime">
              <span className="flex items-center gap-2">
                <AntMark />
                <span className="font-display text-2xl uppercase">AntCrawler</span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close-button" aria-label="Close menu" className="p-2">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-col gap-2 p-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={`font-display text-5xl uppercase leading-tight ${
                      location.pathname === l.to ? "text-lime" : ""
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto p-8 flex flex-col gap-4">
              <button
                onClick={() => {
                  setOpen(false);
                  openBooking();
                }}
                data-testid="nav-mobile-book-button"
                className="btn-brutal-lime w-full"
              >
                Book a Consultation
              </button>
              <a href="tel:+17723324038" data-testid="nav-mobile-call-button" className="btn-brutal-white w-full">
                <Phone size={16} /> 772-332-4038
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="h-16 md:h-20" />
    </>
  );
}
